/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useClassOfServiceList.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing Class of Services list state and data fetching.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-02 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useNotifications } from '../../../hooks/useNotifications';
import { useI18n } from '../../../hooks/useI18n';
import { fetchClassOfServices } from '../api/classOfServicesApi';
import { ListClassOfServicesParams, ClassOfService } from '../types';

interface UseClassOfServiceListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for Class of Services list management
 * Implements Domain Contextual Filtering pattern
 */
export function useClassOfServiceList(options: UseClassOfServiceListOptions = {}) {
  const { domainId, onRefreshNeeded } = options;
  const { showError } = useNotifications();
  const { t } = useI18n();

  const fetchData = useCallback(async (params: Omit<ListClassOfServicesParams, 'domainId'>) => {
    try {
      const result = await fetchClassOfServices({
        ...params,
        domainId
      }, t);

      const data: ClassOfService[] = Array.isArray(result.data) ? result.data : [];
      const pagination = result.pagination || { page: 0, limit: 0, total: 0, totalPages: 0 };

      return {
        data,
        pagination: {
          page: pagination.page ?? params.page,
          limit: pagination.limit ?? params.limit,
          total: pagination.total ?? data.length,
          totalPages: pagination.totalPages ?? 0
        }
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('classOfServices.messages.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, domainId]);

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (domainId !== undefined && onRefreshNeeded) {
      onRefreshNeeded();
    }
  }, [domainId]);

  return {
    fetchData,
    domainId
  };
}

